import React, { useState } from 'react';
import { SegmentedToggle } from '../common/SegmentedToggle';
import CoachView1 from '../../assets/images/CoachView1.jpeg';
import CoachView2 from '../../assets/images/CoachView2.jpeg';
import CoachView3 from '../../assets/images/CoachView3.jpeg';
import PlayerView1 from '../../assets/images/PlayerView1.jpeg';
import PlayerView2 from '../../assets/images/PlayerView2.jpeg';

const TOURS = {
  coach: {
    label: 'Coaches',
    screens: [
      {
        title: 'Sortable Statistics',
        description: 'Filter and compare players by the metrics that actually matter, verified by Presto Sports.',
        image: CoachView2,
      },
      {
        title: 'Team Rosters',
        description: 'Every roster with division, conference, record, and player details in one place.',
        image: CoachView1,
      },
      {
        title: 'Follow Players',
        description: 'Track the players you care about and see their latest stats and video.',
        image: CoachView3,
      },
    ],
  },
  player: {
    label: 'Players',
    screens: [
      {
        title: 'Highlight Video',
        description: 'Show coaches your swing, arm, and glove with video right on your profile.',
        image: PlayerView1,
      },
      {
        title: 'Your Profile & Stats',
        description: 'Bio, season stats, previous teams, awards, and academics, all in one place.',
        image: PlayerView2,
      },
    ],
  },
};

export const ProductTourSection = () => {
  const [audience, setAudience] = useState('coach');
  const [screenIndex, setScreenIndex] = useState(0);
  const { screens } = TOURS[audience];

  const handleAudienceChange = (next) => {
    setAudience(next);
    setScreenIndex(0);
  };

  return (
    <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="mb-2">
              <span className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest">
                Product Tour
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink">
              See JUCO Pipeline <span className="text-jp-accent">in Action</span>
            </h2>
          </div>
          <SegmentedToggle
            ariaLabel="Product tour audience"
            options={Object.entries(TOURS).map(([key, tour]) => ({ value: key, label: tour.label }))}
            value={audience}
            onChange={handleAudienceChange}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-6">
          {/* Screen list: clickable cards that switch the screenshot */}
          <div>
            <p className="flex items-center gap-1.5 text-jp-muted text-xs font-semibold uppercase tracking-wider mb-3">
              <svg className="w-4 h-4 text-jp-accent" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              Click a screen to preview
            </p>
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-1 px-1">
              {screens.map((screen, index) => {
                const isActive = index === screenIndex;
                return (
                  <button
                    key={screen.title}
                    type="button"
                    onClick={() => setScreenIndex(index)}
                    aria-pressed={isActive}
                    className={`group flex-shrink-0 w-64 lg:w-auto flex items-center gap-3 text-left rounded-xl border-2 p-4 cursor-pointer transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent ${
                      isActive
                        ? 'border-jp-accent bg-jp-accent/10 shadow-md'
                        : 'border-jp-outline/50 bg-jp-card shadow-sm hover:border-jp-accent hover:shadow-md hover:-translate-y-0.5'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold mb-1 transition-colors duration-300 ${isActive ? 'text-jp-accent-ink' : 'text-jp-ink group-hover:text-jp-accent-ink'}`}>
                        {screen.title}
                      </p>
                      <p className="text-jp-muted text-sm leading-relaxed">{screen.description}</p>
                    </div>
                    <span
                      aria-hidden="true"
                      className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? 'bg-jp-accent text-jp-navy'
                          : 'border border-jp-outline text-jp-accent-ink group-hover:bg-jp-accent group-hover:text-jp-navy group-hover:border-jp-accent'
                      }`}
                    >
                      <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Screenshot in a browser frame */}
          <div className="rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-jp-line bg-jp-section">
              <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
            </div>
            <div className="relative aspect-[16/10] bg-[#0B1220]">
              {screens.map((screen, index) => (
                <img
                  key={screen.title}
                  src={screen.image}
                  alt={`JUCO Pipeline ${screen.title} screen`}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                    index === screenIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductTourSection;
